import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { trackEvent } from '../utils/analytics';
import { Icon, ICON_DEFAULTS } from '../utils/icons';
import './ShareButton.css';

/**
 * Share a product. Phones open their own share sheet (Instagram, WhatsApp,
 * Viber, Messenger… whatever is installed); desktops get a small menu.
 * Every link is the product's clean address in the shopper's language,
 * tagged with where it was shared (utm_source) so Analytics shows which
 * networks bring buyers. The preview card itself comes from the server.
 */
export const shareUrl = (path, { lang, defaultLocale, source }) => {
  const url = new URL(path, window.location.origin);
  if (lang && defaultLocale && lang !== defaultLocale) url.searchParams.set('lang', lang);
  if (source) {
    url.searchParams.set('utm_source', source);
    url.searchParams.set('utm_medium', 'social');
    url.searchParams.set('utm_campaign', 'share');
  }
  return url.toString();
};

const TARGETS = [
  ['whatsapp', 'WhatsApp', (u, text) => `https://wa.me/?text=${encodeURIComponent(`${text} ${u}`)}`],
  ['viber', 'Viber', (u, text) => `viber://forward?text=${encodeURIComponent(`${text} ${u}`)}`],
  ['facebook', 'Facebook', (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`],
  ['telegram', 'Telegram', (u, text) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(text)}`],
  ['x', 'X', (u, text) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(text)}`],
];

const ShareButton = ({ path, title, text, itemId }) => {
  const { t, i18n } = useTranslation();
  const { defaultLocale } = useSiteSettings();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapper = useRef(null);
  const lang = (i18n.language || '').split('-')[0];
  const link = (source) => shareUrl(path, { lang, defaultLocale, source });
  const message = [title, text].filter(Boolean).join(' — ');

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!wrapper.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const track = (method) => trackEvent('share', { method, content_type: 'product', item_id: itemId });

  const onClick = async () => {
    const coarse = window.matchMedia?.('(pointer: coarse)').matches;
    if (navigator.share && coarse) {
      try {
        await navigator.share({ title, text: message, url: link('share_sheet') });
        track('share_sheet');
      } catch (_e) { /* cancelled */ }
      return;
    }
    setOpen((v) => !v);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link('copy_link'));
      setCopied(true);
      track('copy_link');
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) { /* clipboard blocked */ }
  };

  return (
    <div className="share-menu" ref={wrapper}>
      <button type="button" className="share-btn" onClick={onClick} aria-haspopup="menu" aria-expanded={open}
        title={t('productDetail.share', 'Share')} aria-label={t('productDetail.share', 'Share')}>
        <Icon.Share size={ICON_DEFAULTS.size} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
        <span>{t('productDetail.share', 'Share')}</span>
      </button>
      {open && (
        <div className="share-menu__panel" role="menu">
          {TARGETS.map(([key, label, build]) => (
            <a key={key} role="menuitem" className="share-menu__item" href={build(link(key), message)}
              target="_blank" rel="noopener noreferrer" onClick={() => { track(key); setOpen(false); }}>
              {label}
            </a>
          ))}
          <button type="button" role="menuitem" className="share-menu__item" onClick={copy}>
            {copied ? t('share.copied', 'Link copied') : t('share.copy', 'Copy link')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
